import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneIcon from "@mui/icons-material/Tune";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import {
 filterSuggestionItems,
 insertOrUpdateBlock,
} from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import {
 SuggestionMenuController,
 getDefaultReactSlashMenuItems,
 useCreateBlockNote,
 FilePanelController,
 FilePanel,
 UploadTab,
 FormattingToolbar,
 FormattingToolbarController,
 getFormattingToolbarItems,
 FileReplaceButton,
 useBlockNoteEditor,
 useSelectedBlocks,
 useComponentsContext,
} from "@blocknote/react";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { NotificationImportant as NotificationImportantIcon } from "@mui/icons-material";
import { Quiz as QuizIcon } from "@mui/icons-material";
import { Add as AddIcon } from "@mui/icons-material";
import { BackHand as BackHandIcon } from "@mui/icons-material";
import { Article as ArticleIcon } from "@mui/icons-material";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import { DrawOutlined as DrawOutlinedIcon } from "@mui/icons-material";
import { ScoreOutlined as ScoreOutlinedIcon } from "@mui/icons-material";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import { buildTrainingSchema } from "./trainingBlockSpecs";
import { Checkpoint } from "./Checkpoint";
import { Quiz } from "./Quiz";
import { PDF } from "./PDF";
import { DocumentUpload } from "./DocumentUpload";
import { Signature } from "./Signature";
import { ManagerInput } from "./ManagerInput";
import { SwipeDeck } from "./SwipeDeck";
import { VideoEmbed } from "./VideoEmbed";
import LoadSpinner from "../../../utilities/LoadSpinner";
import {
 API_get_learning_file,
 API_get_learning_quiz_detail,
} from "../../../constants";
import { fetchLmsMappableCertificateTypes } from "../../../utilities/certificateTypes";
import {
 getRightworkAPI,
} from "../requests/utilities/requests";
import { toast } from "react-toastify";
import QuizEditorModal from "./QuizEditorModal";
import SwipeDeckInputsModal from "./SwipeDeckInputsModal";
import { Form } from "./Form";
import FormEditorModal from "./FormEditorModal";
import { Roleplay } from "./Roleplay";
import RoleplayEditorModal from "./RoleplayEditorModal";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import DeleteWarningModal from "./DeleteWarningModal";
import AiContentGeneratorModal from "./AiContentGeneratorModal";
import { filterInvalidTables, removeAllTables } from "./blockNoteUtils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomCategorySelect from "./CustomCategorySelect";
import SettingToggleCard from "./SettingToggleCard";
import {
  embeddedQuizUuids,
  testOutBlockingRecord,
  testOutQuizOptions,
} from "./testOutRules";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
 uploadLearningFile,
 denormalizeEditorDeckForPreview,
} from "./swipeDeckHelpers";
import MediaLibraryModal from "./MediaLibraryModal";
import DirectAssignabilityToggle, {
  setDirectAssignability,
} from "./DirectAssignabilityToggle";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import LearningContentPreviewModal from "./LearningContentPreviewModal";
import LearningPreviewMenuButton from "./LearningPreviewMenuButton";


// Tab body for the upload-only file panel. Wraps the default UploadTab and owns
// the loading flag it expects. Lives outside the component so it isn't
// re-created on every render.
function UploadOnlyTabPanel({ block }) {
  const [, setLoading] = React.useState(false);
  return <UploadTab block={block} setLoading={setLoading} />;
}

// Lazy-load the swipe deck player so its bundle is only fetched when an admin
// actually opens a preview. Defined after imports (module body code, not an
// import statement) to satisfy the `import/first` lint rule.
const SwipeDeckPlayerLazy = React.lazy(() =>
 import("../take_training_employee/swipe_deck_player/SwipeDeckPlayer"),
);

// Default media blocks that show the formatting toolbar's "replace file" button.
const FILE_BLOCK_TYPES = ["image", "video", "audio", "file"];

// Normalization poll cadence for just-uploaded videos (mirrors
// SwipeCardVideoEditor). Capped at ~30 min — transcodes hard-stop at 20, so
// past that the row is stuck and there's nothing left to wait for.
const VIDEO_STATUS_POLL_MS = 4000;
const VIDEO_STATUS_POLL_MAX_TICKS = 450;

// Drop-in replacement for BlockNote's default FileReplaceButton (which renders
// the default FilePanel with the Embed-URL tab, bypassing our FilePanelController
// and the durable-upload pipeline). Same behavior, but its popover shows only
// the Upload tab. Mirrors @blocknote/react v0.38's FileReplaceButton source.
function UploadOnlyFileReplaceButton() {
  const Components = useComponentsContext();
  const editor = useBlockNoteEditor();
  const selectedBlocks = useSelectedBlocks(editor);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    setIsOpen(false);
  }, [selectedBlocks]);

  const block = selectedBlocks.length === 1 ? selectedBlocks[0] : undefined;
  if (
    !block ||
    !FILE_BLOCK_TYPES.includes(block.type) ||
    !editor.isEditable ||
    !Components
  ) {
    return null;
  }

  const tooltip =
    editor?.dictionary?.formatting_toolbar?.file_replace?.tooltip?.[block.type] ||
    editor?.dictionary?.formatting_toolbar?.file_replace?.tooltip?.file ||
    "Replace file";

  return (
    <Components.Generic.Popover.Root opened={isOpen} position="bottom">
      <Components.Generic.Popover.Trigger>
        <Components.FormattingToolbar.Button
          className="bn-button"
          onClick={() => setIsOpen(!isOpen)}
          isSelected={isOpen}
          mainTooltip={tooltip}
          label={tooltip}
          icon={<SwapHorizIcon style={{ fontSize: 18 }} />}
        />
      </Components.Generic.Popover.Trigger>
      <Components.Generic.Popover.Content
        className="bn-popover-content bn-panel-popover"
        variant="panel-popover"
      >
        <FilePanel
          block={block}
          tabs={[
            {
              name: editor?.dictionary?.file_panel?.upload?.title || "Upload",
              tabPanel: <UploadOnlyTabPanel block={block} />,
            },
          ]}
        />
      </Components.Generic.Popover.Content>
    </Components.Generic.Popover.Root>
  );
}

// Custom formatting toolbar: the default buttons, but with the file-replace
// button swapped for the upload-only one (so toolbar-initiated replaces also
// skip the Embed-URL tab).
function UploadOnlyFormattingToolbar() {
  return (
    <FormattingToolbar>
      {getFormattingToolbarItems().map((item) =>
        item.type === FileReplaceButton ? (
          <UploadOnlyFileReplaceButton key={item.key || "fileReplace"} />
        ) : (
          item
        )
      )}
    </FormattingToolbar>
  );
}


function SectionInputsModal2(props) {
 const style = {
   position: "absolute",
   inset: 0,
   margin: "auto",
   width: "96%",
   height: "94%",
   maxWidth: "1280px",
   maxHeight: "94vh",
   bgcolor: "background.paper",
   borderRadius: "16px",
   boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
   display: "flex",
   flexDirection: "column",
   overflow: "hidden",
   border: "none",
 };


 // "Make conditional" checked with no quiz chosen yet. Once a quiz is chosen, the
 // section's test_out_quiz is what keeps the box checked.
 const [conditionalPending, setConditionalPending] = React.useState(false);
 const [conditionalError, setConditionalError] = React.useState(false);
 React.useEffect(() => {
   setConditionalPending(false);
   setConditionalError(false);
 }, [props.open, props.modalDataSection?.uuid]);

 // The settings panel starts open only when this section has a setting on. Re-runs
 // once the section has loaded and when a different section opens, but not on each
 // checkbox click. The AI studio hides everything in the panel, so it has no panel.
 const hasSettingsPanel = !props.hideCategory || !props.hideAssetEmbeds;
 const [showSettings, setShowSettings] = React.useState(false);
 React.useEffect(() => {
   if (props.open && !props.isLoading) {
     setShowSettings(
       Boolean(props.modalDataSection?.test_out_quiz) ||
         Boolean(props.modalDataSection?.is_directly_assignable)
     );
   }
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [props.open, props.isLoading, props.modalDataSection?.uuid]);

 const [open, setOpen] = React.useState(false);
 const handleOpen = () => setOpen(true);
 const handleClose = () => setOpen(false);


 const [openDeleteWarningModal, setOpenDeleteWarningModal] =
   React.useState(false);
 const handleOpenDeleteWarningModal = () => setOpenDeleteWarningModal(true);
 const handleCloseDeleteWarningModal = () => setOpenDeleteWarningModal(false);


 const [openAiContentModal, setOpenAiContentModal] = React.useState(false);
 const [openPreview, setOpenPreview] = React.useState(false);
 const [previewDevice, setPreviewDevice] = React.useState("desktop");
 const [previewSectionSnapshot, setPreviewSectionSnapshot] = React.useState(null);
 const [libraryOpen, setLibraryOpen] = React.useState(false);
 const handleOpenAiContentModal = () => setOpenAiContentModal(true);

 // Quiz editor opens in create mode by default; flips to true when editing an
 // already-embedded quiz in place (see openQuizEditorForUuid).
 const [quizEditMode, setQuizEditMode] = React.useState(false);

 // Create-new Form editor (opened from the "Create Form" slash item)
 const [openForm, setOpenForm] = React.useState(false);
 const [formEditorSeed, setFormEditorSeed] = React.useState({});

 // Create-new practice scenario editor (opened from the "Create Practice Scenario" slash item)
 const [openRoleplay, setOpenRoleplay] = React.useState(false);
 const [roleplayEditorSeed, setRoleplayEditorSeed] = React.useState({});

 // Create-new Swipe Deck editor (opened from the "Create Swipe Deck" slash item)
 const [openCreateSwipeDeck, setOpenCreateSwipeDeck] = React.useState(false);
 // null → create mode; { uuid } → edit an existing deck in place (modal self-fetches).
 const [swipeDeckEditSeed, setSwipeDeckEditSeed] = React.useState(null);

 // Tracks an in-progress "edit existing embedded asset" action so the save
 // handler re-stamps that specific block in place (handling renames) instead
 // of inserting a fresh embed at the cursor. Shape: { kind, blockId }.
 const editingAssetBlockRef = React.useRef(null);
 // Holds the in-progress deck when the editor's Preview button is clicked, so
 // the fullscreen player can render the (possibly unsaved) deck locally.
 const [swipeDeckPreviewState, setSwipeDeckPreviewState] = React.useState(null);
 const handleCloseAiContentModal = () => setOpenAiContentModal(false);

 // Background polls for videos still normalizing, cleared on unmount.
 const videoPollTimersRef = React.useRef(new Set());
 React.useEffect(
   () => () => {
     videoPollTimersRef.current.forEach(clearInterval);
     videoPollTimersRef.current.clear();
   },
   []
 );

 // Mirrors SwipeCardVideoEditor's normalization poll: the worker replaces the
 // uploaded key with the normalized mp4 and deletes the original, so the
 // preview URL a just-inserted block holds would go dead in the still-open
 // editor once processing finishes. Poll until ready, then re-point matching
 // blocks (and the save map) at the fresh signed URL. On "failed" the original
 // file is kept server-side, so the existing preview URL stays valid and we
 // just stop.
 const startVideoNormalizationPoll = (fileUUID, originalUrl) => {
   let ticks = 0;
   const id = setInterval(async () => {
     ticks += 1;
     if (ticks > VIDEO_STATUS_POLL_MAX_TICKS) {
       clearInterval(id);
       videoPollTimersRef.current.delete(id);
       return;
     }
     try {
       const res = await getRightworkAPI(API_get_learning_file, {
         company_id: props.homeCompany,
         learning_file_uuid: fileUUID,
       });
       const lf = res?.data?.redux?.payload?.learning_file;
       const status = lf?.video_status || null;
       if (status !== "ready" && status !== "failed") return;
       clearInterval(id);
       videoPollTimersRef.current.delete(id);
       if (status !== "ready" || !lf?.file) return;
       // Keep the save path working if the author saves after the swap: the
       // block's URL is about to become the new one, so it needs a map entry.
       try {
         const map = JSON.parse(
           localStorage.getItem("mapSignedUrlToFileUuid") || "{}"
         );
         map[lf.file] = fileUUID;
         localStorage.setItem("mapSignedUrlToFileUuid", JSON.stringify(map));
       } catch (e) {
         // Ignore parse/quota hiccups; save falls back to the original entry.
       }
       const repointBlocks = (blocks) => {
         (blocks || []).forEach((block) => {
           if (block.props?.url === originalUrl) {
             editor.updateBlock(block, {
               props: { ...block.props, url: lf.file },
             });
           }
           repointBlocks(block.children);
         });
       };
       repointBlocks(editor?.document);
     } catch (e) {
       // transient — keep polling
     }
   }, VIDEO_STATUS_POLL_MS);
   videoPollTimersRef.current.add(id);
 };


 const uploadFile = async (media) => {
   try {
     // Shared LMS upload pipeline (presign -> S3 PUT -> notify -> signed read
     // URL). The notify step kicks off server-side normalization for videos,
     // so raw camera uploads (4K / 40+ Mbps) get re-encoded to a streamable
     // mp4 instead of buffering forever on restaurant wifi. Sections allow
     // larger videos than swipe cards, so the card size limit isn't enforced.
     const { uuid: fileUUID, url: fileUrl, videoStatus } =
       await uploadLearningFile(props.homeCompany, media, {
         enforceVideoSizeLimit: false,
       });


     // NOTE: Because we use signed URLs, whatever we get back from our API will
     //       only be valid for ~1 hour. So whenever the Blocknote compontent is saved,
     //       we must send up the file UUID to our server instead of the image URL.
     let mapSignedUrlToFileUuid =
       localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
     if (!mapSignedUrlToFileUuid) {
       mapSignedUrlToFileUuid = {};
     } else {
       mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);
     }
     mapSignedUrlToFileUuid[fileUrl] = fileUUID;
     localStorage.setItem(
       "mapSignedUrlToFileUuid",
       JSON.stringify(mapSignedUrlToFileUuid)
     );


     // A video that still needs transcoding: keep the just-inserted block's
     // preview URL fresh once the worker swaps in the normalized mp4.
     if (videoStatus === "pending" || videoStatus === "processing") {
       startVideoNormalizationPoll(fileUUID, fileUrl);
     }

     // Finally
     return fileUrl;
   } catch (error) {
     console.error("Error uploading file:", error);
     throw error;
   }
 };


 const removeAllBlocks = () => {
   if (editor) {
     const allBlockIDsToRemove = editor.document.map((block) => block.id);
     editor.removeBlocks(allBlockIDsToRemove);
   }
 };


 const loadAllBlocks = () => {
   if (editor) {
     const allBlocksToLoad = props.modalDataSection.as_json;

     if (!allBlocksToLoad || !Array.isArray(allBlocksToLoad)) {
       console.error("Invalid blocks data:", allBlocksToLoad);
       return;
     }

     // Filter out invalid tables entirely (safer than trying to repair)
     const filteredBlocks = filterInvalidTables(allBlocksToLoad);

     // Replace the whole document in one atomic op (mirrors BlocksPreview).
     // The previous `removeBlocks()` + `insertBlocks(blocks,
     // editor.document[0]?.id, "before")` dance read editor.document
     // immediately after mutating it, and BlockNote can hand back a stale
     // snapshot whose first block id is already gone — insertBlocks then
     // throws "Block with ID … not found". When that happened the catch
     // re-ran the identical broken insertBlocks, which threw a SECOND time
     // uncaught, escaping the effect and white-screening the whole app.
     // replaceBlocks(editor.document, …) captures the live doc and swaps it
     // in a single transaction, so there is no stale reference to miss.
     try {
       editor.replaceBlocks(editor.document, filteredBlocks);
     } catch (error) {
       console.error("Error loading blocks:", error);
       // Last resort: recursively remove ALL table blocks (including nested
       // ones). Wrapped so a second failure can never escape this effect.
       try {
         const blocksWithoutTables = removeAllTables(allBlocksToLoad);
         editor.replaceBlocks(editor.document, blocksWithoutTables);
       } catch (fallbackError) {
         console.error("Failed to load section content", fallbackError);
       }
     }
   }
 };


 // Mirrors the TrackEditorModal viewer pattern (TrackEditorModal.js:401-407):
 // load blocks once a fetch finishes while the modal is open. We can't gate on
 // an as_json transition here because callers (e.g. TrackEditorModal's
 // auto-load of section[0][0] when the track editor opens) write to
 // modalDataSection while this modal is still closed — the transition would
 // be missed and the editor would render blank on first open.
 React.useEffect(() => {
   if (!props.open) return;
   if (props.modalEdit !== true) return;
   if (props.isLoading) return;
   if (!(props.modalDataSection?.as_json?.length > 0)) return;
   // loadAllBlocks now does a single atomic replaceBlocks(editor.document,
   // …), so no separate removeAllBlocks() first — clearing then immediately
   // reading editor.document was what produced the stale block reference.
   loadAllBlocks();
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [props.isLoading, props.open]);


 // Snapshot the loaded "directly assignable" value so Save can tell whether it
 // changed (it's persisted on Save, like the other settings — never on toggle).
 const originalAssignableRef = React.useRef(false);
 React.useEffect(() => {
   if (props.open) {
     originalAssignableRef.current = !!props.modalDataSection?.is_directly_assignable;
   }
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [props.open, props.modalDataSection?.uuid]);

 const cloneBlocksForPreview = (blocks) => {
   if (!Array.isArray(blocks)) return null;
   try {
     return JSON.parse(JSON.stringify(blocks));
   } catch (error) {
     console.error("Unable to clone section preview blocks", error);
     return null;
   }
 };

 const hasPreviewableBlocks = (blocks) =>
   Array.isArray(blocks) &&
   blocks.some((block) => {
     if (!block) return false;
     if (block.type && block.type !== "paragraph") return true;
     if (Array.isArray(block.content) && block.content.length > 0) return true;
     if (Array.isArray(block.children) && block.children.length > 0) return true;
     return false;
   });

 const openSectionPreview = (device) => {
   const editorBlocks = editor ? cloneBlocksForPreview(editor.document) : null;
   const savedBlocks = cloneBlocksForPreview(props.modalDataSection?.as_json);
   const previewBlocks = editorBlocks !== null
     ? editorBlocks
     : hasPreviewableBlocks(savedBlocks)
       ? savedBlocks
       : null;
   const snapshot = {
     ...(props.modalDataSection || {}),
     _previewKey: `section-editor-preview-${Date.now()}`,
   };

   if (previewBlocks) {
     snapshot.as_json = previewBlocks;
   } else if (!snapshot.uuid) {
     snapshot.as_json = [];
   } else {
     delete snapshot.as_json;
   }

   setPreviewDevice(device);
   setPreviewSectionSnapshot(snapshot);
   setOpenPreview(true);
 };


 function handleChange(event) {
   const { name, value } = event.target;
   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     [name]: value,
   }));
 }


 function handleCategoryChange(option) {
   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     category: option,
   }));
 }


 const quizzesData = props.quizzesData;
 const homeCompany = props.homeCompany;
 // Captured up here so block render callbacks can reference it via
 // closure. Inside `render: (props) => ...`, the callback's own
 // `props` shadows the outer component's `props`, so `props.language`
 // there reads BlockNote block props (which has no language field) and
 // resolves to undefined.
 const sectionEditorLanguage = props.language;

 // Certificate-type catalog for the documentUpload mapping picker. Held in a
 // ref (not state): useCreateBlockNote memoizes the schema on first render,
 // so a state value would stay frozen at [] inside the block render closures.
 // The ref holds a deferred ({promise, resolve}) created SYNCHRONOUSLY on the
 // first render: documentUpload blocks can mount in the same commit as this
 // component, and child effects run before parent effects, so a promise
 // assigned inside our own useEffect would not exist yet when the block
 // subscribes (it would read null once and never retry). The fetch effect
 // below resolves the deferred; extra resolves on later homeStore runs are
 // no-ops, which is fine because the catalog is company-level and a
 // cross-company store switch remounts this whole surface.
 const certificateTypeOptionsPromiseRef = React.useRef(null);
 if (certificateTypeOptionsPromiseRef.current === null) {
   let resolveOptions;
   const promise = new Promise((resolve) => {
     resolveOptions = resolve;
   });
   certificateTypeOptionsPromiseRef.current = { promise, resolve: resolveOptions };
 }
 React.useEffect(() => {
   if (!props.homeStore) return;
   // Shared fetch + offerable-types predicate (see certificateTypes.js);
   // hidden types are excluded so a new mapping can't produce documents
   // invisible in the overview.
   fetchLmsMappableCertificateTypes(props.homeStore)
     .then(({ options }) => {
       certificateTypeOptionsPromiseRef.current.resolve(options);
     })
     .catch((error) => {
       console.error("Failed to fetch certificate types:", error);
       certificateTypeOptionsPromiseRef.current.resolve([]);
     });
 }, [props.homeStore]);


 // Blocknote Custom Stuff


 // Block configs (propSchema/content) come from the shared trainingBlockSpecs
 // module; only the edit-mode renders live here (they close over this
 // component's state: modal openers, upload helpers, etc.).
 // Memoized with [] deliberately: useCreateBlockNote below only reads the
 // schema on first render (see its comment), so rebuilding on re-renders was
 // pure waste — the render closures were already frozen at first render.
 const schema = React.useMemo(() => buildTrainingSchema({
   quiz: (props) => (
     <Quiz
       {...props}
       quizzesData={quizzesData}
       homeCompany={homeCompany}
       type="Preview"
       onEditAsset={(uuid) =>
         openQuizEditorForUuid(uuid, props.block.id)
       }
     />
   ),
   checkpoint: (props) => <Checkpoint {...props} />,
   pdf: (props) => <PDF {...props} uploadFile={uploadFile} />,
   documentUpload: (props) => (
     <DocumentUpload
       {...props}
       uploadFile={uploadFile}
       certificateTypeOptionsPromiseRef={certificateTypeOptionsPromiseRef}
     />
   ),
   signature: (props) => (
     <Signature
       {...props}
       homeCompany={homeCompany}
       mode="edit"
     />
   ),
   managerInput: (props) => (
     <ManagerInput
       {...props}
       homeCompany={homeCompany}
       mode="edit"
     />
   ),
   swipeDeck: (props) => (
     <SwipeDeck
       {...props}
       homeCompany={homeCompany}
       mode="edit"
       language={sectionEditorLanguage}
       onEditAsset={(uuid) =>
         openSwipeDeckEditorForUuid(uuid, props.block.id)
       }
     />
   ),
   form: (props) => (
     <Form
       {...props}
       homeCompany={homeCompany}
       mode="edit"
       onEditAsset={(uuid) =>
         openFormEditorForUuid(uuid, props.block.id)
       }
     />
   ),
   roleplay: (props) => (
     <Roleplay
       {...props}
       homeCompany={homeCompany}
       mode="edit"
       onEditAsset={(uuid) =>
         openRoleplayEditorForUuid(uuid, props.block.id)
       }
     />
   ),
   videoEmbed: (props) => <VideoEmbed {...props} mode="edit" />,
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }), []);


 // Slash menu item to insert custom blocks


 const insertQuiz = (editor) => ({
   title: "Quiz",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "quiz",
     });
   },
   aliases: ["quiz", "test", "exam", "questionnaire"],
   group: "Assessments & Embeds",
   icon: <QuizIcon style={{ fontSize: 18 }} />,
   subtext: "Insert a quiz",
 });


 const insertCreateQuiz = (editor) => ({
   title: "Create Quiz",
   onItemClick: () => {
     editingAssetBlockRef.current = null;
     setQuizEditMode(false);
     props.setModalDataQuiz(props.default_data_quiz);
     handleOpen();
   },
   aliases: ["quiz", "test", "exam", "questionnaire"],
   group: "Assessments & Embeds",
   icon: <AddIcon style={{ fontSize: 18 }} />,
   subtext: "Create a quiz that can be inserted after creation",
 });


 const insertForm = (editor) => ({
   title: "Form",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "form",
     });
   },
   aliases: ["form", "rubric", "checklist", "note log", "feedback", "survey"],
   group: "Assessments & Embeds",
   icon: <DescriptionOutlinedIcon style={{ fontSize: 18 }} />,
   subtext: "Insert an existing form (rubric, checklist, note log)",
 });


 const insertCreateForm = (editor) => ({
   title: "Create Form",
   onItemClick: () => {
     editingAssetBlockRef.current = null;
     setFormEditorSeed({});
     setOpenForm(true);
   },
   aliases: ["form", "rubric", "checklist", "feedback", "create form"],
   group: "Assessments & Embeds",
   icon: <AddIcon style={{ fontSize: 18 }} />,
   subtext: "Create a form that can be inserted after creation",
 });


 const insertRoleplay = (editor) => ({
   title: "Practice Scenario",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "roleplay",
     });
   },
   aliases: ["practice scenario", "roleplay", "role play", "conversation", "practice", "scenario"],
   group: "Assessments & Embeds",
   icon: <ConnectWithoutContactIcon style={{ fontSize: 18 }} />,
   subtext: "Insert an AI conversation practice scenario",
 });


 const insertCreateRoleplay = (editor) => ({
   title: "Create Practice Scenario",
   onItemClick: () => {
     editingAssetBlockRef.current = null;
     setRoleplayEditorSeed({});
     setOpenRoleplay(true);
   },
   aliases: ["practice scenario", "roleplay", "role play", "conversation", "practice", "create roleplay"],
   group: "Assessments & Embeds",
   icon: <AddIcon style={{ fontSize: 18 }} />,
   subtext: "Create a practice scenario that can be inserted after creation",
 });


 const insertCheckpoint = (editor) => ({
   title: "Checkpoint",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "checkpoint",
     });
   },
   aliases: ["checkpoint", "stop", "pause"],
   group: "Workflow",
   icon: <BackHandIcon style={{ fontSize: 18 }} />,
   subtext: "Insert a checkpoint (requires manager check off to continue)",
 });


 const insertAlert = (editor) => ({
   title: "Alert",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "alert",
     });
   },
   aliases: [
     "alert",
     "callout",
     "notification",
     "emphasize",
     "warning",
     "error",
     "info",
     "success",
   ],
   group: "Others",
   icon: <NotificationImportantIcon style={{ fontSize: 18 }} />,
   subtext: "Insert an alert (click icon to change alert type)",
 });


 const insertPDF = (editor) => ({
   title: "PDF Document",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "pdf",
     });
   },
   aliases: ["pdf", "document", "file", "embed"],
   group: "Media",
   icon: <ArticleIcon style={{ fontSize: 18 }} />,
   subtext: "Embed a PDF document",
 });


 const insertDocumentUpload = (editor) => ({
   title: "Document Upload",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "documentUpload",
     });
   },
   aliases: ["upload", "document upload", "file upload", "certificate"],
   group: "Workflow",
   icon: <UploadFileIcon style={{ fontSize: 18 }} />,
   subtext: "Let employees upload documents",
 });


 const insertSignature = (editor) => ({
   title: "Signature",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "signature",
     });
   },
   aliases: ["signature", "sign", "esign", "esignature", "e-signature", "acknowledge", "acknowledgement", "policy"],
   group: "Workflow",
   icon: <DrawOutlinedIcon style={{ fontSize: 18 }} />,
   subtext: "Collect an acknowledgement or signature",
 });


 const insertManagerInput = (editor) => ({
   title: "Manager Input",
   onItemClick: () => {
     insertOrUpdateBlock(editor, {
       type: "managerInput",
     });
   },
   aliases: ["manager input", "score", "grade", "offline test", "manager score"],
   group: "Workflow",
   icon: <ScoreOutlinedIcon style={{ fontSize: 18 }} />,
   subtext: "Manager inputs scores or values for employees",
 });


 const insertSwipeDeck = (editor) => ({
   title: "Swipe Deck",
   onItemClick: () => {
     // Inserts an unconfigured block — the SwipeDeck render in edit
     // mode shows a picker placeholder so the admin selects an existing
     // deck inline. Mirrors the Quiz insertion flow.
     insertOrUpdateBlock(editor, {
       type: "swipeDeck",
     });
   },
   aliases: ["swipe deck", "deck", "cards", "tiktok", "stories"],
   group: "Assessments & Embeds",
   icon: <StyleOutlinedIcon style={{ fontSize: 18 }} />,
   subtext: "Insert a swipe deck of bite-sized learning cards",
 });

 const insertCreateSwipeDeck = (editor) => ({
   title: "Create Swipe Deck",
   onItemClick: () => {
     editingAssetBlockRef.current = null;
     setSwipeDeckEditSeed(null);
     setOpenCreateSwipeDeck(true);
   },
   aliases: ["swipe deck", "deck", "cards", "create swipe deck", "tiktok", "stories"],
   group: "Assessments & Embeds",
   icon: <AddIcon style={{ fontSize: 18 }} />,
   subtext: "Create a swipe deck that can be inserted after creation",
 });


 const insertAiContent = (editor) => ({
   // "Text" (not "Content") because it only produces text blocks — be explicit
   // so authors don't expect images / quizzes / etc.
   title: "Generate Text with AI",
   onItemClick: () => {
     handleOpenAiContentModal();
   },
   aliases: ["ai", "generate", "ask ai", "text", "content", "gpt", "assistant"],
   group: "Others",
   icon: <AutoAwesomeIcon style={{ fontSize: 18 }} />,
   subtext: "Generate text with AI",
 });


 const insertImageFromLibrary = (editor) => ({
   title: "Image from Library",
   onItemClick: () => {
     setLibraryOpen(true);
   },
   aliases: ["library", "image", "photo", "media", "reuse"],
   group: "Media",
   icon: <CollectionsOutlinedIcon style={{ fontSize: 18 }} />,
   subtext: "Insert an existing upload, AI image, or document photo",
 });


 const insertVideoEmbed = (editor) => ({
   title: "Video Embed",
   onItemClick: () => {
     // Inserts an unconfigured block — the VideoEmbed render in edit mode shows
     // a paste-URL panel so the admin pastes a YouTube link inline.
     insertOrUpdateBlock(editor, {
       type: "videoEmbed",
     });
   },
   aliases: ["video", "youtube", "embed", "media"],
   group: "Media",
   icon: <OndemandVideoIcon style={{ fontSize: 18 }} />,
   subtext: "Embed a YouTube video",
 });


const editor = useCreateBlockNote({
  schema,
  uploadFile,
});

 // Upload-only file panel: drop the "Embed" (external URL) tab so every image /
 // video goes through our storage (durable file_uuid) rather than a fragile
 // third-party link. "Image from Library" remains the reuse path. Applies to
 // all media blocks (image, video, audio) since they share one panel.
 const renderUploadOnlyFilePanel = (panelProps) => (
   <FilePanel
     {...panelProps}
     tabs={[
       {
         name: editor?.dictionary?.file_panel?.upload?.title || "Upload",
         tabPanel: <UploadOnlyTabPanel block={panelProps.block} />,
       },
     ]}
   />
 );

 // Mirrors the uploadFile flow: the picked LearningFile's signed URL goes
 // into a standard image block, and the url->uuid map entry lets
 // post_learning_section swap in the durable file_uuid at save (signed
 // URLs expire in ~1 hour, so the block must persist by uuid).
 const handleLibrarySelect = ({ uuid, url, name }) => {
   if (!editor || !url) return;
   let mapSignedUrlToFileUuid;
   try {
     mapSignedUrlToFileUuid = JSON.parse(
       localStorage.getItem("mapSignedUrlToFileUuid") || "{}"
     );
   } catch (e) {
     // Corrupt map — reset it rather than fail the insert. Losing stale
     // entries is fine; in-flight editors re-record on their next save.
     mapSignedUrlToFileUuid = {};
   }
   mapSignedUrlToFileUuid[url] = uuid;
   localStorage.setItem(
     "mapSignedUrlToFileUuid",
     JSON.stringify(mapSignedUrlToFileUuid)
   );

   insertOrUpdateBlock(editor, {
     type: "image",
     props: {
       url,
       name: name || "",
     },
   });

   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     as_json: editor.document,
   }));
 };

 // Make conditional. The blocks are live: the editor writes each change to as_json.
 const testOutCurrent = props.modalDataSection?.test_out_quiz;
 const testOutBlocking = testOutBlockingRecord(props.modalDataSection?.as_json);
 const quizzesInSection = embeddedQuizUuids(props.modalDataSection?.as_json);
 const testOutOptions = testOutQuizOptions(
   (props.quizzesData || []).filter((quiz) => !quizzesInSection.has(String(quiz.uuid)))
 );
 const testOutCurrentIsInside =
   Boolean(testOutCurrent) && quizzesInSection.has(String(testOutCurrent.uuid));
 const testOutSelectedLabel = testOutCurrent
   ? testOutOptions.find((o) => o.uuid === testOutCurrent.uuid)?.label || testOutCurrent.name
   : "";
 // A section that can't be skipped never reads as conditional, unless it already
 // has a link, which stays checked so the author can remove it.
 const isConditional =
   Boolean(testOutCurrent) || (conditionalPending && !testOutBlocking);

 const conditionalReadyToSave = () => {
   if (props.hideAssetEmbeds || !isConditional || testOutCurrent) return true;
   setConditionalError(true);
   // The error is in the settings panel, which may be closed.
   setShowSettings(true);
   toast.error("Choose a quiz or uncheck Make conditional.", { autoClose: 3000 });
   return false;
 };

 // Shown beside the settings icon while the panel is closed, so an author can
 // still see that this section can be skipped.
 const settingsOn = props.hideAssetEmbeds
   ? []
   : [
       isConditional && "Conditional",
       props.modalDataSection?.is_directly_assignable && "Directly assignable",
     ].filter(Boolean);

 const handleQuizSaved = (savedQuiz) => {
   if (!savedQuiz || !editor) {
     return;
   }

   const newProps = {
     quizSelected: savedQuiz,
     // Drag-proof primitive mirror (object props are wiped on drag).
     quizUuid: savedQuiz?.uuid || "",
     quizName: savedQuiz?.name || "",
   };

   const editing = editingAssetBlockRef.current;
   if (editing && editing.kind === "quiz" && editing.blockId) {
     // Re-stamp the block we were editing in place (name may have changed)
     // rather than inserting a new embed at the cursor.
     restampEmbedBlock(editing.blockId, newProps);
   } else {
     insertOrUpdateBlock(editor, { type: "quiz", props: newProps });
   }
   editingAssetBlockRef.current = null;

   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     as_json: editor.document,
   }));
 };

 const handleFormSaved = (savedForm) => {
   if (!savedForm || !editor) {
     return;
   }

   const newProps = {
     formSelected: { uuid: savedForm.uuid, name: savedForm.name || "" },
     // Drag-proof primitive mirror (object props are wiped on drag).
     formUuid: savedForm.uuid || "",
     formName: savedForm.name || "",
   };

   const editing = editingAssetBlockRef.current;
   if (editing && editing.kind === "form" && editing.blockId) {
     restampEmbedBlock(editing.blockId, newProps);
   } else {
     insertOrUpdateBlock(editor, { type: "form", props: newProps });
   }
   editingAssetBlockRef.current = null;

   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     as_json: editor.document,
   }));
 };

 const handleRoleplaySaved = (savedRoleplay) => {
   if (!savedRoleplay || !editor) {
     return;
   }

   const newProps = {
     roleplaySelected: { uuid: savedRoleplay.uuid, name: savedRoleplay.name || "" },
     // Drag-proof primitive mirror (object props are wiped on drag).
     roleplayUuid: savedRoleplay.uuid || "",
     roleplayName: savedRoleplay.name || "",
   };

   const editing = editingAssetBlockRef.current;
   if (editing && editing.kind === "roleplay" && editing.blockId) {
     restampEmbedBlock(editing.blockId, newProps);
   } else {
     insertOrUpdateBlock(editor, { type: "roleplay", props: newProps });
   }
   editingAssetBlockRef.current = null;

   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     as_json: editor.document,
   }));
 };

 const handleSwipeDeckSaved = (savedDeck) => {
   if (!savedDeck || !editor) {
     return;
   }

   const newProps = {
     swipeDeckSelected: {
       uuid: savedDeck.uuid,
       name: savedDeck.name || "",
     },
     // Drag-proof primitive mirror (object props are wiped on drag).
     swipeDeckUuid: savedDeck.uuid || "",
     swipeDeckName: savedDeck.name || "",
   };

   const editing = editingAssetBlockRef.current;
   if (editing && editing.kind === "swipeDeck" && editing.blockId) {
     restampEmbedBlock(editing.blockId, newProps);
   } else {
     insertOrUpdateBlock(editor, { type: "swipeDeck", props: newProps });
   }
   editingAssetBlockRef.current = null;

   props.setModalDataSection((prevModalData) => ({
     ...prevModalData,
     as_json: editor.document,
   }));
 };

 // Re-stamp an existing embed block's props in place (preserving its position
 // and any other props), used after editing an already-embedded asset so a
 // renamed asset's card label refreshes.
 const restampEmbedBlock = (blockId, nextProps) => {
   const existing = editor.getBlock(blockId);
   if (!existing) {
     // Block was removed while the editor was open — nothing to re-stamp.
     return;
   }
   editor.updateBlock(existing, {
     props: { ...existing.props, ...nextProps },
   });
 };

 // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
 // Edit-an-embedded-asset entry points (wired into each block's Edit button).
 // Each opens the asset's standalone editor seeded with current content and
 // records the block being edited so the save handler re-stamps it in place.

 const openQuizEditorForUuid = async (quizUuid, blockId) => {
   if (!quizUuid) return;
   try {
     const res = await getRightworkAPI(API_get_learning_quiz_detail, {
       company_id: props.homeCompany,
       learning_quiz_uuid: quizUuid,
       include_section_usage: true,
     });
     const loadedQuiz =
       res?.status === 200 ? res.data?.redux?.payload?.learning_quiz : null;
     if (!loadedQuiz) {
       // Failed/empty load (deleted quiz, permission error, network blip).
       // Bail without opening — otherwise the editor would open against the
       // previous modalDataQuiz and a Save could re-stamp the block with the
       // wrong quiz.
       throw new Error("Quiz detail not returned");
     }
     // Only commit to opening (and marking this an in-place edit) once the
     // requested quiz actually loaded.
     editingAssetBlockRef.current = { kind: "quiz", blockId };
     setQuizEditMode(true);
     props.setModalDataQuiz(loadedQuiz);
     handleOpen();
   } catch (error) {
     console.error("Error loading quiz for editing:", error);
     toast.error("Couldn't load this quiz. Please try again.");
   }
 };

 const openFormEditorForUuid = (formUuid, blockId) => {
   if (!formUuid) return;
   editingAssetBlockRef.current = { kind: "form", blockId };
   // Seed with just the uuid; FormEditorModal self-fetches its detail.
   setFormEditorSeed({ uuid: formUuid });
   setOpenForm(true);
 };

 const openRoleplayEditorForUuid = (roleplayUuid, blockId) => {
   if (!roleplayUuid) return;
   editingAssetBlockRef.current = { kind: "roleplay", blockId };
   // Seed with just the uuid; RoleplayEditorModal self-fetches its detail.
   setRoleplayEditorSeed({ uuid: roleplayUuid });
   setOpenRoleplay(true);
 };

 const openSwipeDeckEditorForUuid = (deckUuid, blockId) => {
   if (!deckUuid) return;
   editingAssetBlockRef.current = { kind: "swipeDeck", blockId };
   // Seed with just the uuid; SwipeDeckInputsModal self-fetches its detail.
   setSwipeDeckEditSeed({ uuid: deckUuid });
   setOpenCreateSwipeDeck(true);
 };


 const handleAiContentInsert = (generatedBlocks) => {
   if (editor && generatedBlocks && generatedBlocks.length > 0) {
     // Get current cursor position
     const currentBlock = editor.getTextCursorPosition()?.block;

     if (currentBlock) {
       // Insert generated blocks at current position
       editor.insertBlocks(
         generatedBlocks,
         currentBlock.id,
         "before"
       );

       // Remove the current block (the one with the / command) if it's empty
       const currentBlockContent = currentBlock.content || [];
       const isEmpty = currentBlockContent.length === 0 ||
                       (currentBlockContent.length === 1 && !currentBlockContent[0].text);

       if (isEmpty && currentBlock.type === "paragraph") {
         editor.removeBlocks([currentBlock.id]);
       }
     } else {
       // If no cursor position, insert at the end
       const lastBlock = editor.document[editor.document.length - 1];
       if (lastBlock) {
         editor.insertBlocks(
           generatedBlocks,
           lastBlock.id,
           "after"
         );
       }
     }
   }
 };


 const getDistinctCategories = () => {
   if (!props.sectionsData || props.sectionsData?.length === 0) {
     return [];
   }


   const categories = props.sectionsData.map((section) => section.category);
   const distinctCategories = [...new Set(categories)].filter(Boolean);


   return distinctCategories;
 };


 const sectionDistinctCategories = getDistinctCategories();
 const sectionDistinctCategoryCount = sectionDistinctCategories?.length || 0;


 const [showCreateCategory, setShowCreateCategory] = React.useState(sectionDistinctCategoryCount === 0);


 React.useEffect(() => {
   if (props.open) {
     setShowCreateCategory(sectionDistinctCategoryCount === 0);
   }
 }, [props.open, sectionDistinctCategoryCount]);


 // Auto-scroll the editor column while dragging a block near its top or bottom.
 // BlockNote uses native drag and drop and never scrolls, and the browser's
 // own edge band is ~20px, so dragging a block from the bottom to the top was
 // effectively impossible. Speed ramps up the closer the cursor gets, and stays
 // at full speed with the cursor over the header or footer.
 const modalBoxRef = React.useRef(null);
 const scrollContainerRef = React.useRef(null);
 React.useEffect(() => {
   if (!props.open) return undefined;

   const EDGE_ZONE_PX = 80;
   const MAX_SPEED_PX = 24;
   let speed = 0;
   let frameId = null;

   const tick = () => {
     const container = scrollContainerRef.current;
     if (!container || speed === 0) {
       frameId = null;
       return;
     }
     container.scrollTop += speed;
     frameId = requestAnimationFrame(tick);
   };

   const stop = () => {
     speed = 0;
     if (frameId !== null) {
       cancelAnimationFrame(frameId);
       frameId = null;
     }
   };

   const handleDragOver = (event) => {
     const container = scrollContainerRef.current;
     if (!container) return;
     // Stacked editors (Quiz, Form, ...) portal outside this Box; a drag in
     // one of them must not scroll the section modal underneath.
     if (!modalBoxRef.current?.contains(event.target)) {
       stop();
       return;
     }
     const rect = container.getBoundingClientRect();
     if (event.clientX < rect.left || event.clientX > rect.right) {
       stop();
       return;
     }
     const topEdge = rect.top;
     const bottomEdge = rect.bottom;
     const y = event.clientY;
     let nextSpeed = 0;
     if (y < topEdge + EDGE_ZONE_PX) {
       const depth = Math.min(1, (topEdge + EDGE_ZONE_PX - y) / EDGE_ZONE_PX);
       nextSpeed = -Math.ceil(depth * MAX_SPEED_PX);
     } else if (y > bottomEdge - EDGE_ZONE_PX) {
       const depth = Math.min(1, (y - (bottomEdge - EDGE_ZONE_PX)) / EDGE_ZONE_PX);
       nextSpeed = Math.ceil(depth * MAX_SPEED_PX);
     }
     speed = nextSpeed;
     if (speed !== 0 && frameId === null) {
       frameId = requestAnimationFrame(tick);
     }
   };

   // Dragging out of the browser window stops dragover events; stop too.
   const handleDragLeave = (event) => {
     if (event.relatedTarget === null) stop();
   };

   document.addEventListener("dragover", handleDragOver, true);
   document.addEventListener("dragleave", handleDragLeave, true);
   document.addEventListener("drop", stop, true);
   document.addEventListener("dragend", stop, true);
   return () => {
     stop();
     document.removeEventListener("dragover", handleDragOver, true);
     document.removeEventListener("dragleave", handleDragLeave, true);
     document.removeEventListener("drop", stop, true);
     document.removeEventListener("dragend", stop, true);
   };
 }, [props.open]);


 return (
   <>
     <Modal
       open={props.open}
       aria-labelledby="section-modal-title"
       aria-describedby="section-modal-description"
     >
       <Box sx={style} ref={modalBoxRef}>
         {!props.isLoading ? (
           <>
             {/* Header */}
             <div className="relative shrink-0 bg-white border-b border-gray-200 px-6 py-4 z-[9999]">
               <div className="flex items-center justify-between">
                 <div className="flex items-center gap-3">
                   <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                     <ViewHeadlineIcon className="text-violet-600" style={{ fontSize: "20px" }} />
                   </div>
                   <div>
                     <h3 className="text-2xl font-bold text-slate-900">
                       {props.modalEdit ? "Edit Section" : "Create New Section"}
                     </h3>
                     <p className="text-sm text-slate-600">
                       {props.modalEdit
                         ? "Update section content and details"
                         : "Build rich learning content with text, images, quizzes, and more"}
                     </p>
                   </div>
                 </div>
                 <div className="flex items-center gap-2 shrink-0">
                   <LearningPreviewMenuButton
                     variant="button"
                     onSelect={openSectionPreview}
                   />
                   <button
                     className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0"
                     onClick={() => {
                       props.handleClose();
                       removeAllBlocks();
                       // removeAllBlocks() empties this editor, which fires onChange
                       // and writes as_json:[] into the shared modalDataSection — so
                       // without a reload the parent's <SectionPreview> blanks to
                       // white. Reload the selected section from the server (also
                       // discards any unsaved edits, which is the intent of Close).
                       props.refresh_section && props.refresh_section();
                     }}
                   >
                     <CloseIcon />
                   </button>
                 </div>
               </div>
             </div>

             <div className="relative flex-1 min-h-0 flex overflow-hidden">
                 <div ref={scrollContainerRef} className="flex-1 min-w-0 overflow-auto py-6">
             {/* Optional caller-supplied banner — used by the AI
                 Training Studio to warn that a section belongs to a
                 shared module ("This module appears in 3 tracks:
                 Server, Lead Server, Host — changes affect all"). Other
                 callers pass nothing and the banner doesn't render. */}
             {props.extraHeaderBanner && (
               <div className="px-6 mb-6">
                 {props.extraHeaderBanner}
               </div>
             )}
                 <div className="px-6 space-y-6">
               {/* Section Name */}
               <div>
                 <div className="flex items-center justify-between gap-2 mb-2 min-h-[30px]">
                   <label className="block text-sm font-medium text-gray-700">
                     Section Name *
                   </label>
                   {hasSettingsPanel && !showSettings && (
                     <div className="flex items-center gap-2 min-w-0">
                       {settingsOn.length > 0 && (
                         <span className="text-xs font-medium text-violet-700">
                           {settingsOn.join(", ")}
                         </span>
                       )}
                       <IconButton
                         size="small"
                         onClick={() => setShowSettings(true)}
                         title="Show settings"
                       >
                         <TuneIcon fontSize="small" />
                       </IconButton>
                     </div>
                   )}
                 </div>
                 <TextField
                   placeholder="What do you want to name this section? (e.g., How to serve a table)"
                   fullWidth
                   variant="outlined"
                   size="small"
                   name="name"
                   value={props.modalDataSection?.name}
                   onChange={handleChange}
                   InputProps={{
                     sx: {
                       backgroundColor: "white",
                       borderRadius: "8px",
                       height: "45px",
                       "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                         borderColor: "#6d28d9",
                       },
                     },
                   }}
                 />
               </div>

               {/* Content Editor */}
               <div>
                 <div className="flex items-center justify-between mb-3">
                   <label className="block text-sm font-medium text-gray-700">
                     Section Content
                   </label>
                   <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                     Type "/" for commands
                   </div>
                 </div>
                 <div className="border border-gray-200 rounded-lg overflow-hidden">
                   <div className="min-h-[350px] p-4 bg-white">
                     {editor && (
                       <BlockNoteView
                         editor={editor}
                         editable={true}
                         slashMenu={false}
                         filePanel={false}
                         formattingToolbar={false}
                         theme="light"
                         onChange={() => {
                           // Saves the document JSON to state.
                           props.setModalDataSection((prevModalData) => ({
                             ...prevModalData,
                             as_json: editor.document,
                           }));
                         }}
                       >
                         {/* Upload-only file panel (no external-URL Embed tab). */}
                         <FilePanelController
                           filePanel={renderUploadOnlyFilePanel}
                         />
                         {/* Formatting toolbar with an upload-only file-replace
                             button (the default one renders the Embed tab). */}
                         <FormattingToolbarController
                           formattingToolbar={UploadOnlyFormattingToolbar}
                         />
                         {/* Replaces the default Slash Menu. */}
                         <SuggestionMenuController
                           triggerCharacter={"/"}
                           getItems={async (query) => {
                             // BlockNote renders the menu in array order, starting
                             // a NEW group header every time `group` changes — so
                             // same-group items must be contiguous or you get
                             // duplicate headers (the "two Media groups" bug). We
                             // (1) read group names off the default items via
                             // anchors (never hardcode a name BlockNote might
                             // rename), (2) re-group a few items, drop File, and
                             // (3) stable-sort so each group renders once, with
                             // "Others" pinned last.
                             const defaultItems =
                               getDefaultReactSlashMenuItems(editor);
                             const groupOf = (pred, fallback) => {
                               const it = defaultItems.find(pred);
                               return it ? it.group : fallback;
                             };
                             const mediaGroup = groupOf(
                               (it) => it.key === "image" || it.title === "Image",
                               "Media"
                             );
                             const basicGroup = groupOf(
                               (it) =>
                                 it.key === "paragraph" ||
                                 it.title === "Paragraph",
                               "Basic blocks"
                             );
                             const othersGroup = groupOf(
                               (it) => it.key === "emoji" || it.title === "Emoji",
                               "Others"
                             );

                             // Drop the default "File" block from the menu.
                             const trimmedDefaults = defaultItems.filter(
                               (it) => !(it.key === "file" || it.title === "File")
                             );
                             // Move "Table" into Basic blocks (it ships separately).
                             trimmedDefaults.forEach((it) => {
                               if (it.key === "table" || it.title === "Table") {
                                 it.group = basicGroup;
                               }
                             });

                             // Custom items, grouped to merge with the right
                             // default groups (Media, Others). Splice "Image from
                             // Library" right after "Image" so the two image
                             // options stay adjacent within the Media group.
                             const libraryItem = insertImageFromLibrary(editor);
                             libraryItem.group = mediaGroup;
                             const imageIdx = trimmedDefaults.findIndex(
                               (it) => it.key === "image" || it.title === "Image"
                             );
                             if (imageIdx !== -1) {
                               trimmedDefaults.splice(imageIdx + 1, 0, libraryItem);
                             }
                             // Splice "Video Embed" right after the default
                             // "Video" item so the two video options stay
                             // adjacent within the Media group.
                             const videoEmbedItem = insertVideoEmbed(editor);
                             videoEmbedItem.group = mediaGroup;
                             const videoIdx = trimmedDefaults.findIndex(
                               (it) => it.key === "video" || it.title === "Video"
                             );
                             if (videoIdx !== -1) {
                               trimmedDefaults.splice(videoIdx + 1, 0, videoEmbedItem);
                             }
                             const pdfItem = insertPDF(editor);
                             pdfItem.group = mediaGroup;
                             const alertItem = insertAlert(editor);
                             alertItem.group = othersGroup;
                             const aiItem = insertAiContent(editor);
                             aiItem.group = othersGroup;

                             const combined = [
                               ...trimmedDefaults,
                               // Fallback if the default Image item wasn't found.
                               ...(imageIdx === -1 ? [libraryItem] : []),
                               // Fallback if the default Video item wasn't found.
                               ...(videoIdx === -1 ? [videoEmbedItem] : []),
                               pdfItem,
                               // hideAssetEmbeds: callers that manage quizzes /
                               // swipe decks / forms outside the document (the AI
                               // studio holds them as separate payload assets)
                               // drop the embed items — inserting one here would
                               // commit an unresolved embed. Self-contained blocks
                               // (checkpoint, signature, uploads, alert, AI text)
                               // stay.
                               ...(props.hideAssetEmbeds
                                 ? []
                                 : [
                                     insertQuiz(editor),
                                     insertCreateQuiz(editor),
                                     insertForm(editor),
                                     insertCreateForm(editor),
                                     insertSwipeDeck(editor),
                                     insertCreateSwipeDeck(editor),
                                     insertRoleplay(editor),
                                     insertCreateRoleplay(editor),
                                   ]),
                               insertCheckpoint(editor),
                               insertManagerInput(editor),
                               insertDocumentUpload(editor),
                               insertSignature(editor),
                               alertItem,
                               aiItem,
                             ];

                             // Stable-sort so each group is contiguous (one
                             // header), ordered by first appearance, with Others
                             // pinned last. combined.length > any real index, so
                             // Others sorts after everything; ties keep order.
                             const firstSeen = new Map();
                             combined.forEach((it, i) => {
                               if (!firstSeen.has(it.group)) {
                                 firstSeen.set(it.group, i);
                               }
                             });
                             const rank = (it) =>
                               it.group === othersGroup
                                 ? combined.length
                                 : firstSeen.get(it.group);
                             const ordered = combined
                               .map((it, i) => [it, i])
                               .sort(
                                 (a, b) =>
                                   rank(a[0]) - rank(b[0]) || a[1] - b[1]
                               )
                               .map((pair) => pair[0]);

                             return filterSuggestionItems(ordered, query);
                           }}
                         />
                       </BlockNoteView>
                     )}
                   </div>
                 </div>
                 {/* <div className="mt-2 text-xs text-gray-500">
                   <div className="flex flex-wrap gap-4">
                     <span>• Type "/" to insert blocks</span>
                     <span>• Use "/quiz" for assessments</span>
                     <span>• Use "/checkpoint" for manager reviews</span>
                     <span>• Use "/alert" for important notices</span>
                   </div>
                 </div> */}
               </div>
                 </div>
                 </div>

                 {/* Right: settings. Over the editor on small screens, where it would squeeze it. */}
                 {hasSettingsPanel && showSettings && (
                   <div className="w-[320px] shrink-0 border-l border-slate-200 bg-white overflow-auto md:absolute md:inset-y-0 md:right-0 md:z-20 md:shadow-xl">
                     <div className="h-14 px-5 border-b border-slate-200 flex items-center justify-between shrink-0">
                       <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                         Section Settings
                       </p>
                       <IconButton
                         size="small"
                         onClick={() => setShowSettings(false)}
                         title="Hide settings"
                       >
                         <ChevronRightIcon fontSize="small" />
                       </IconButton>
                     </div>
                     <div className="px-5 py-5 space-y-6">
                       {/* Category */}
                       {!props.hideCategory && (
                         <div>
                           <div className="flex items-center justify-between mb-2">
                             <label className="block text-sm font-medium text-gray-700">
                               Category
                             </label>
                             <button
                               className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                               onClick={() => {
                                 if (showCreateCategory && sectionDistinctCategories.length > 0) {
                                   setShowCreateCategory(false);
                                 } else {
                                   setShowCreateCategory(true);
                                   props.setModalDataSection((prevData) => ({
                                     ...prevData,
                                     category: "",
                                   }));
                                 }
                               }}
                             >
                               {showCreateCategory ? (
                                 sectionDistinctCategories.length > 0 ? "Back to Options" : ""
                               ) : (
                                 "Create New"
                               )}
                             </button>
                           </div>
                           {showCreateCategory ? (
                             <TextField
                               placeholder="Create a new category"
                               fullWidth
                               variant="outlined"
                               size="small"
                               name="category"
                               value={props.modalDataSection?.category}
                               onChange={handleChange}
                               InputProps={{
                                 sx: {
                                   backgroundColor: "white",
                                   borderRadius: "8px",
                                   height: "45px",
                                   "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                     borderColor: "#6d28d9",
                                   },
                                 },
                               }}
                             />
                           ) : (
                             <CustomCategorySelect
                               options={sectionDistinctCategories}
                               value={props.modalDataSection?.category}
                               onChange={handleCategoryChange}
                               name="Category"
                               placeholder="Select category"
                             />
                           )}
                         </div>
                       )}

                       {!props.hideAssetEmbeds && (
                         <>
                           {/* Make conditional: skip this section with a test-out quiz */}
                           <SettingToggleCard
                             checked={isConditional}
                             disabled={Boolean(testOutBlocking) && !testOutCurrent}
                             onChange={(checked) => {
                               setConditionalPending(checked);
                               setConditionalError(false);
                               if (!checked) {
                                 props.setModalDataSection((prev) => ({
                                   ...prev,
                                   test_out_quiz: null,
                                 }));
                               }
                             }}
                             title="Make conditional"
                             description={
                               testOutBlocking
                                 ? `This section has ${testOutBlocking}, so employees can't skip it.`
                                 : "Employees skip this section if they pass a test-out quiz."
                             }
                           >
                             {isConditional && !testOutBlocking && (
                               testOutOptions.length === 0 && !testOutCurrent ? (
                                 <p className="text-xs text-slate-700">
                                   {testOutQuizOptions(props.quizzesData).length > 0
                                     ? "The only test-out quiz is inside this section, and a section can't be skipped by a quiz inside it."
                                     : "No test-out quizzes yet. Turn on Test-out quiz in a quiz's settings first."}
                                 </p>
                               ) : (
                                 <div>
                                   <p className="text-xs font-medium text-slate-700 mb-1">
                                     Skip if employee passes
                                   </p>
                                   <CustomCategorySelect
                                     key={`${props.modalDataSection?.uuid}-${testOutSelectedLabel}`}
                                     options={testOutOptions.map((o) => o.label)}
                                     value={testOutSelectedLabel}
                                     onChange={(label) => {
                                       const option = testOutOptions.find((o) => o.label === label);
                                       if (!option) return;
                                       setConditionalError(false);
                                       props.setModalDataSection((prev) => ({
                                         ...prev,
                                         test_out_quiz: { uuid: option.uuid, name: option.name },
                                       }));
                                     }}
                                     name="Skip if employee passes"
                             compactText
                                     placeholder="Choose a quiz"
                                   />
                                   {testOutCurrentIsInside ? (
                                     <p className="text-xs mt-1 text-rose-700">
                                       A section can't be skipped by a quiz inside it, because everyone needs to take that quiz.
                                     </p>
                                   ) : conditionalError ? (
                                     <p role="alert" className="text-xs mt-1 text-rose-700">
                                       Choose a quiz or uncheck Make conditional.
                                     </p>
                                   ) : (
                                     <p className="text-xs mt-1 text-slate-500">
                                       Applies everywhere this section is used. Employees who don't pass still take it.
                                     </p>
                                   )}
                                 </div>
                               )
                             )}
                           </SettingToggleCard>

                           {/* Make directly assignable */}
                           <DirectAssignabilityToggle
                             value={props.modalDataSection?.is_directly_assignable}
                             onChange={(val) =>
                               props.setModalDataSection((prev) => ({
                                 ...prev,
                                 is_directly_assignable: val,
                               }))
                             }
                           />
                         </>
                       )}
                     </div>
                   </div>
                 )}
             </div>


             {/* Footer */}
             <div className="relative shrink-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
               <div className="flex items-center justify-end gap-3">
                 {/* hideDeleteAndDuplicate: callers whose save fn has no
                     duplicate/delete semantics (the AI studio routes saves
                     into generated_payload_json and removes sections on
                     its own canvas) hide both buttons — otherwise
                     Duplicate would silently plain-save and Delete would
                     call an unpassed delete_learning_section. */}
                 {props.modalEdit
                   && props.type !== "Preview"
                   && !props.hideDeleteAndDuplicate && (
                    <button
                      className="w-10 h-10 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg flex items-center justify-center transition-colors"
                      onClick={async () => {
                        // Same explicit-null contract as Save below:
                        // keep the modal open on a reported failure.
                        if (!conditionalReadyToSave()) return;
                        const duplicated =
                          await props.post_learning_section(true);
                        if (duplicated === null) return;
                        removeAllBlocks();
                        props.handleClose();
                      }}
                    >
                      <ContentCopyIcon style={{ fontSize: "18px" }} />
                    </button>
                 )}


                 {props.modalEdit
                 && props.type !== "Preview"
                 && !props.hideDeleteAndDuplicate ? (
                   <button
                     className="px-6 py-2.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium transition-colors"
                     onClick={handleOpenDeleteWarningModal}
                   >
                     Delete Section
                   </button>
                 ) : (
                   <button
                     className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                     onClick={() => {
                       props.handleClose();
                       removeAllBlocks();
                       // See the Close (X) handler above: clearing the editor
                       // blanks the shared as_json, so reload the selected section
                       // to restore the preview (and discard unsaved edits).
                       props.refresh_section && props.refresh_section();
                     }}
                   >
                     Cancel
                   </button>
                 )}


                 <button
                   className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
                   onClick={async () => {
                     if (!conditionalReadyToSave()) return;
                     const savedSection = await props.post_learning_section();
                     // Explicit null = the save fn reports failure (the
                     // studio's payload-update path toasts the error and
                     // returns null on a 409 / validation / network
                     // failure). Keep the modal open so the edits
                     // survive for a retry. Legacy callers resolve
                     // undefined on success AND failure — those keep
                     // the historical always-close behavior, since
                     // undefined is indistinguishable from success.
                     if (savedSection === null) return;

                     // Persist the "make directly assignable" setting on Save
                     // (only when it changed) — like every other setting. Not
                     // shown in the AI studio (hideAssetEmbeds).
                     const desiredAssignable =
                       !!props.modalDataSection?.is_directly_assignable;
                     if (
                       !props.hideAssetEmbeds &&
                       savedSection?.uuid &&
                       desiredAssignable !== originalAssignableRef.current
                     ) {
                       await setDirectAssignability({
                         companyId: props.homeCompany,
                         assetType: "section",
                         assetUuid: savedSection.uuid,
                         isAssignable: desiredAssignable,
                       });
                     }

                     removeAllBlocks();

                     // If creating a new section from ModulePreview, call the callback with the returned section
                     if (props.onSaveSuccess && !props.modalEdit && savedSection) {
                       props.onSaveSuccess(savedSection);
                     }

                     props.handleClose();
                     if (props.type === "Preview" && props.modalEdit) {
                       // Only refresh when editing existing section, not when creating new
                       removeAllBlocks();
                       props.removeAllBlocks();
                       props.refresh_section();
                     }
                   }}
                 >
                   Save Section
                 </button>
               </div>
             </div>
           </>
         ) : (
           <div className="flex items-center justify-center flex-1">
             <LoadSpinner />
           </div>
         )}
       </Box>
     </Modal>


     <QuizEditorModal
       open={open}
       handleClose={() => {
         // Cancel/close — drop any in-progress edit target so a later save
         // can't re-stamp a stale block, and reset edit mode so it can't
         // linger into the next open.
         editingAssetBlockRef.current = null;
         setQuizEditMode(false);
         handleClose();
       }}
       modalEdit={quizEditMode}
       isLoading={false}
       modalDataQuiz={props.modalDataQuiz}
       setModalDataQuiz={props.setModalDataQuiz}
       post_learning_quiz={props.post_learning_quiz}
       delete_learning_quiz={props.delete_learning_quiz}
       quizzesData={props.quizzesData}
       homeCompany={props.homeCompany}
       sectionsData={props.sectionsData}
       modulesData={props.modulesData}
       onSaveSuccess={handleQuizSaved}
     />


     <FormEditorModal
       open={openForm}
       handleClose={() => {
         editingAssetBlockRef.current = null;
         setOpenForm(false);
       }}
       modalDataForm={formEditorSeed}
       homeCompany={props.homeCompany}
       sectionsData={props.sectionsData}
       modulesData={props.modulesData}
       onSaveSuccess={handleFormSaved}
     />

     <RoleplayEditorModal
       open={openRoleplay}
       handleClose={() => {
         editingAssetBlockRef.current = null;
         setOpenRoleplay(false);
       }}
       modalDataRoleplay={roleplayEditorSeed}
       homeCompany={props.homeCompany}
       sectionsData={props.sectionsData}
       modulesData={props.modulesData}
       onSaveSuccess={handleRoleplaySaved}
     />

     {/* Swipe deck editor. modalDataDeck=null → create mode (auto-embeds the new
         deck on save, mirroring Create Quiz/Form); { uuid } → edit an existing
         embedded deck in place. onSaved → handleSwipeDeckSaved inserts (create)
         or re-stamps (edit) the block. */}
     <SwipeDeckInputsModal
       open={openCreateSwipeDeck}
       onClose={() => {
         editingAssetBlockRef.current = null;
         setOpenCreateSwipeDeck(false);
       }}
       modalDataDeck={swipeDeckEditSeed}
       homeCompany={props.homeCompany}
       sectionsData={props.sectionsData}
       modulesData={props.modulesData}
       onSaved={handleSwipeDeckSaved}
       onPreview={(deckInEditor) => {
         // Editor preview — pass the in-progress (possibly unsaved) deck
         // object directly so the player skips a fetch and renders local
         // state with answer keys.
         setSwipeDeckPreviewState({
           previewDeck: denormalizeEditorDeckForPreview(deckInEditor),
         });
       }}
     />

     {swipeDeckPreviewState && (
       <React.Suspense
         fallback={
           <div className="fixed inset-0 z-[1400] bg-black/95 flex items-center justify-center">
             <div className="text-white/80 text-sm">Loading…</div>
           </div>
         }
       >
         <SwipeDeckPlayerLazy
           deckUuid={swipeDeckPreviewState.deckUuid}
           previewDeck={swipeDeckPreviewState.previewDeck}
           previewMode
           companyId={props.homeCompany}
           onClose={() => setSwipeDeckPreviewState(null)}
         />
       </React.Suspense>
     )}

     <LearningContentPreviewModal
       open={openPreview}
       onClose={() => {
         setOpenPreview(false);
         setPreviewSectionSnapshot(null);
       }}
       previewType="section"
       item={previewSectionSnapshot || props.modalDataSection}
       initialDevice={previewDevice}
       homeCompany={props.homeCompany}
       quizzesData={props.quizzesData}
       language={props.language}
     />

     <MediaLibraryModal
       open={libraryOpen}
       onClose={() => setLibraryOpen(false)}
       companyId={props.homeCompany}
       onSelect={handleLibrarySelect}
     />

     <AiContentGeneratorModal
       open={openAiContentModal}
       handleClose={handleCloseAiContentModal}
       homeCompany={props.homeCompany}
       onInsert={handleAiContentInsert}
     />

     <DeleteWarningModal
       open={openDeleteWarningModal}
       handleClose={handleCloseDeleteWarningModal}
       type="section"
       delete_function={props.delete_learning_section}
       handleCloseInputsModal={props.handleClose}
       removeAllBlocks={removeAllBlocks}
     />
   </>
 );
}


export default SectionInputsModal2;
